import { Alert, AlertSection, AlertType } from '@aloft/toolkit-shared';

import {
  getPropertyIdFromPropertyGridCellKey,
  parsePropertyGridCellKey,
} from '../../components/PropertyGrid/types.ts';
import {
  ALERT_HOVER_PREVIEW_CELL_ATTR,
  COMPARABLES_ALERTS_TOAST_CLASS_NAME,
} from '../../hooks/useToaster/constants.ts';
import {
  setActiveID,
  setHoveredAlertType,
  setHoveredCostApproachPropertyId,
  setHoveredOrderDetailsFieldKey,
  setHoveredPropertyGridCellKey,
} from '../../redux/features/referenceBarUISlice.ts';
import { AppDispatch } from '../../redux/types.ts';
import { isCostApproachAlertFixRoute } from './costApproachSliderFocus.ts';
import { parseOrderDetailsTargetCellKey } from '../orderDetails/orderDetailsFieldFocus.ts';
import { propertyIdToReferenceViewId } from '../referenceView/index.ts';

export const shouldPreserveAlertHoverPreview = (relatedTarget: HTMLElement | null): boolean => {
  if (!relatedTarget) {
    return false;
  }
  if (relatedTarget.closest(`.${COMPARABLES_ALERTS_TOAST_CLASS_NAME}`)) {
    return true;
  }
  if (relatedTarget.closest('.alerts-sheet-alert-item')) {
    return true;
  }
  if (relatedTarget.closest(`[${ALERT_HOVER_PREVIEW_CELL_ATTR}]`)) {
    return true;
  }
  return false;
};

const parseTargetCellKey = (targetCellKey?: string) => {
  if (!targetCellKey) {
    return null;
  }
  const parsed = parsePropertyGridCellKey(targetCellKey);
  return parsed ?? null;
};

export const syncAlertHoverPreview = (
  dispatch: AppDispatch,
  alert: Pick<Alert, 'type' | 'propertyID' | 'fixingInfo'> | null,
) => {
  dispatch(setHoveredAlertType(alert?.type ?? null));
  const targetCellKey = alert?.fixingInfo.targetCellKey;
  const orderDetailsField = targetCellKey
    ? parseOrderDetailsTargetCellKey(targetCellKey)
    : undefined;
  if (orderDetailsField) {
    dispatch(setHoveredPropertyGridCellKey(null));
    dispatch(setHoveredCostApproachPropertyId(null));
    dispatch(setHoveredOrderDetailsFieldKey(orderDetailsField));
    return;
  }
  if (
    alert?.propertyID &&
    !targetCellKey &&
    isCostApproachAlertFixRoute(alert.fixingInfo.pairedCostRouteItem)
  ) {
    dispatch(setHoveredPropertyGridCellKey(null));
    dispatch(setHoveredOrderDetailsFieldKey(null));
    dispatch(setHoveredCostApproachPropertyId(alert.propertyID));
    return;
  }
  dispatch(setHoveredOrderDetailsFieldKey(null));
  dispatch(setHoveredCostApproachPropertyId(null));
  const parsedCellKey = parseTargetCellKey(targetCellKey);
  dispatch(setHoveredPropertyGridCellKey(parsedCellKey));
  if (parsedCellKey) {
    const propertyId = getPropertyIdFromPropertyGridCellKey(parsedCellKey);
    if (propertyId !== 'subject') {
      dispatch(setActiveID(propertyIdToReferenceViewId(propertyId)));
    }
  }
};

export const syncAlertHoverPreviewFromType = (
  dispatch: AppDispatch,
  alertType: AlertType | null,
  alertsPerSection: Record<AlertSection, Alert[]>,
  propertyID?: string,
) => {
  if (!alertType) {
    syncAlertHoverPreview(dispatch, null);
    return;
  }
  const alert = Object.values(alertsPerSection)
    .flat()
    .find(
      (candidate) =>
        candidate.type === alertType &&
        (propertyID === undefined || candidate.propertyID === propertyID),
    );
  syncAlertHoverPreview(dispatch, alert ?? null);
};
